import {PreviewFile} from 'common/src/components/CLFilePreview/CLFilePreview';
import {backgroundProcessService} from 'common/src/modules/backgroundProcess/backgroundProcess.service';
import _map from 'lodash/map';
import _mapKeys from 'lodash/mapKeys';
import _mapValues from 'lodash/mapValues';
import {ExternalLink} from '../../utils/fileUtils/extractExternalLinksFromZip';
import {AdSize} from '../adSize/adSize.types';
import {AdTagPreviewParams, CampaignAdTags} from '../adTags/adTags.types';
import aiApi from '../ai/ai.api';
import {EmbeddingSourceType} from '../ai/ai.enums';
import apiV2Service from '../api/api-v2.service';
import apiService from '../api/api.service';
import {ApiOptions, ParamsObj, PostDataObj} from '../api/apiTypes';
import {createPathParamsUrl} from '../api/createPathParamsUrl';
import {ComplexTextFont} from '../complexText/complexText.types';
import {
  toRemoteDynamicParameter,
  toRemoteDynamicParameters,
} from '../creatives/creatives.dataConverters';
import {DynamicParameter} from '../creatives/creatives.types';
import {
  toClientMapping,
  toClientMeta,
  toRemoteMapping,
} from '../dynamicParameters/dynamicParameters.dataConverters';
import {
  DPDynamicSourceType,
  DynamicParametersType,
  TemplateAssetType,
} from '../dynamicParameters/dynamicParameters.enums';
import {
  DPLayerMapping,
  RemoteDPMapping,
  RemoteDPMeta,
  RemoteTemplateAsset,
  TemplateAsset,
} from '../dynamicParameters/dynamicParameters.types';
import enumsService from '../enums/enums.service';
import {CLEnumType} from '../enums/enums.types';
import {fileUploadApi, uploadFileToS3} from '../fileUpload/fileUpload.api';
import {ImageUploadType, VideoUploadType} from '../fileUpload/fileUpload.types';
import {JobExecutionParams, JobResult, JobTemplateResult} from '../jobs/jobs.types';
import waitForJob from '../jobs/utils/waitForJob';
import {CLMimeType} from '../media/media.types';
import {Movie} from '../movie/movie.types';
import {ProductType} from '../products/products.enums';
import {SharingType} from '../sharing/sharing.enums';
import {DataWithSharingInfo, SharedClient} from '../sharing/sharing.types';
import taxonomyApi from '../taxonomy/taxonomy.api';
import {
  ActivityLogData,
  RemoteTemplateLogItem,
  TemplateActivityLogType,
} from './creativeTemplateActivityLog.types';
import {
  toClientActivityLog,
  toClientCreativeTemplate,
  toClientDynamicParamsNameOptions,
  toClientGeneratedDynamicParamsByFormat,
  toClientTemplateAsset,
} from './creativeTemplates.dataConverters';
import {
  AELayerAssetsMapping,
  CreateTemplateFormRemote,
  CreativeTemplate,
  CreativeTemplateDropDownOptions,
  CreativeTemplateSource,
  CreativeTemplateType,
  DPDynamicSourceOptions,
  DynamicParamNameOptions,
  FormatInstance,
  GeneratedDynamicParams,
  RemoteCreativeTemplate,
  RemoteCreativeTemplateDropDownOptions,
  RemoteDynamicParamNameOptions,
  RemoteGeneratedDynamicParams,
} from './creativeTemplates.types';
import mergeDropDownOptionsObjs from './utils/mergeDropDownOptionsObjs';
import {EntityToRename} from 'common/src/modules/shared/copilot/copilot.types';
import {handleApiError} from '../errorHandling/handleApiError';
import {ApiError} from '../errorHandling/errorHandling.types';
import waitForJobWithoutBGProcess from '../jobs/utils/waitForJobWithoutBGP';

const templatesBasePath = 'Templates';
const aeExtensionBasePath = 'AeExtension';

export interface CreativeTemplatesApiProps {
  types?: CreativeTemplateType[];
  productType?: number;
  getOnlyStylesAttachedToClient?: boolean;
  getOnlyStylesAttachedToAgency?: boolean;
  getStyleInstructions?: boolean;
  styleId?: number;
  includeDynamicParams?: boolean;
}

async function getAll({types, ...restOfParams}: CreativeTemplatesApiProps) {
  const params: typeof restOfParams & {type?: string} = {
    ...restOfParams,
  };

  if (types) {
    params.type = types.map((templateType) => CreativeTemplateType[templateType]).join(',');
  }

  const [
    templateEnums,
    {
      data: [creativeTemplates],
    },
  ] = await Promise.all([
    enumsService.loadEnums(CLEnumType.Templates),
    apiService.executeGetRequest<RemoteCreativeTemplate[][]>(
      `${templatesBasePath}/GetAll`,
      apiService.cleanEmptyValues(params as ParamsObj),
    ),
  ]);

  return _map(creativeTemplates, (creativeTemplate) =>
    toClientCreativeTemplate(creativeTemplate, templateEnums),
  );
}

async function getAllWithPermissions(): Promise<
  DataWithSharingInfo<CreativeTemplate[], 'templates'>
> {
  const [
    templateEnums,
    {
      data: {templates, sharePermissions},
    },
  ] = await Promise.all([
    enumsService.loadEnums(CLEnumType.Templates),
    apiV2Service.executeGetRequest<DataWithSharingInfo<RemoteCreativeTemplate[], 'templates'>>(
      `${templatesBasePath}/GetAll`,
    ),
  ]);

  return {
    sharePermissions: sharePermissions,
    templates: _map(templates, (template) => toClientCreativeTemplate(template, templateEnums)),
  };
}

async function get({
  templateId,
  includeDynamicParams = false,
  includeInstructions = false,
  includeCreativesData = false,
}: {
  templateId: number;
  includeDynamicParams?: boolean;
  includeInstructions?: boolean;
  includeCreativesData?: boolean;
}): Promise<DataWithSharingInfo<CreativeTemplate>> {
  const [
    templateEnums,
    {
      data: {template, sharePermissions},
    },
  ] = await Promise.all([
    enumsService.loadEnums(CLEnumType.Templates),
    apiV2Service.executeGetRequest<DataWithSharingInfo<RemoteCreativeTemplate, 'template'>>(
      `${templatesBasePath}/${templateId}`,
      {templateId, includeDynamicParams, includeInstructions, includeCreativesData},
    ),
  ]);

  return {
    sharePermissions: sharePermissions,
    data: toClientCreativeTemplate(template, templateEnums),
  };
}

async function getTemplateDropDownOptions({
  templateId,
  itemsCount,
}: {
  templateId: number;
  itemsCount: number;
}): Promise<CreativeTemplateDropDownOptions> {
  const {data: dropdownOptionsObjs} = await apiService.executeGetRequest<
    RemoteCreativeTemplateDropDownOptions[]
  >(`${aeExtensionBasePath}/GetDropdownsOptions`, {
    styleId: templateId,
    itemCount: itemsCount,
  });

  return mergeDropDownOptionsObjs(dropdownOptionsObjs);
}

async function getDynamicSourceOptions(
  templateId: number,
  query: {
    supportLocation: boolean;
    supportProduct: boolean;
    supportWeather: boolean;
    supportContent: boolean;
    productType: ProductType;
  },
): Promise<DPDynamicSourceOptions> {
  const [data, taxonomyMapping] = await Promise.all([
    apiV2Service
      .executeGetRequest<DPDynamicSourceOptions>(
        `${templatesBasePath}/${templateId}/DynamicParams/DynamicSources`,
        {...query},
      )
      .then(({data}) => data),
    taxonomyApi.getMapping(),
  ]);

  return _mapValues(data, (options) =>
    options.map((option) => {
      if (!option.taxonomyKey) {
        return {...option, taxonomized: false};
      }

      if (option.sourceType === DPDynamicSourceType.Location) {
        const taxonomy = taxonomyMapping.Location?.[option.taxonomyKey];
        if (taxonomy)
          return {
            ...option,
            uiName: taxonomy,
            taxonomized: true,
          };
      }

      if (option.sourceType === DPDynamicSourceType.Product) {
        const taxonomy = taxonomyMapping.Product?.[option.taxonomyKey];

        if (taxonomy)
          return {
            ...option,
            uiName: taxonomy,
            taxonomized: true,
          };
      }

      if (option.sourceType === DPDynamicSourceType.Content) {
        const taxonomy = taxonomyMapping.Content?.[option.taxonomyKey];

        if (taxonomy)
          return {
            ...option,
            uiName: taxonomy,
            taxonomized: true,
          };
      }

      return {...option, taxonomized: false};
    }),
  );
}

async function validatePublicationAllowed({templateId}: {templateId: number}): Promise<boolean> {
  const {
    data: [publishAllowed],
  } = await apiService.executeGetRequest<boolean[]>(
    `${templatesBasePath}/IsTemplateAllowedForUpdating`,
    {templateId},
  );

  return publishAllowed;
}

function create(templateData: CreateTemplateFormRemote): Promise<number> {
  templateData.source ||= CreativeTemplateSource.AdBuilder;

  return apiV2Service
    .executePostRequest(
      templatesBasePath,
      {},
      {
        ...templateData,
      },
    )
    .then(({data: newTemplateID}: {data: number}) => newTemplateID);
}

function update(templateData: CreateTemplateFormRemote, templateId: number): Promise<number> {
  return apiV2Service
    .executePatchRequest(
      `${templatesBasePath}/${templateId}`,
      {},
      {
        ...templateData,
      },
    )
    .then(() => templateId);
}

async function deleteTemplateClient({
  clientIdToDisconnect,
  templateIdToDisconnect,
}: {
  clientIdToDisconnect: number;
  templateIdToDisconnect: number;
}) {
  return apiService.executeDeleteRequest(`${templatesBasePath}/DeleteTemplateClient`, {
    clientIdToDisconnect,
    templateIdToDisconnect,
  });
}

function deleteTemplates(...templateIds: number[]) {
  return Promise.all(
    templateIds.map((styleId) =>
      apiService.executeDeleteRequest(`${templatesBasePath}/DeleteStyle`, {styleId}),
    ),
  );
}

function duplicateTemplates(...templateIds: number[]): Promise<number[]> {
  return apiService
    .executePostRequest(`${templatesBasePath}/DuplicateStyles`, {
      stylesIdsToDup: templateIds.join(','),
    })
    .then(({data}: {data: number[]}) => data.flat());
}

function addToClient(clientId: number, templateIds: number[]) {
  return Promise.all(
    templateIds.map((templateId) =>
      apiService.executePostRequest<void>(`${templatesBasePath}/AddStyleClient`, {
        styleIdToAttach: templateId,
        clientIdToAttach: clientId,
      }),
    ),
  );
}

function deleteFormat(templateId: number, width: number, height: number) {
  return apiService.executeDeleteRequest(`${templatesBasePath}/DeleteStyleFormat`, {
    styleId: templateId,
    width,
    height,
  });
}

function deleteFormats(templateId: number, formats: FormatInstance[]) {
  return Promise.all(formats.map(({Width, Height}) => deleteFormat(templateId, Width, Height)));
}

async function uploadExternalAd({
  file,
  templateId,
  externalLinks = [],
}: {
  file: File;
  templateId: number;
  externalLinks?: ExternalLink[];
}) {
  const jobName = `Publish Formats to Template ${templateId}`;

  try {
    const {
      data: {
        jobExecutionId: {executionId: jobId},
        validationErrors,
      },
    } = await apiV2Service.uploadFile<{
      jobExecutionId: JobExecutionParams;
      validationErrors: string[];
    }>({
      endpoint: `Publishing/ClinchSDK/${templateId}`,
      params: {},
      file,
      formDataKey: 'zipFile',
      options: {},
      postData: {externalLinksJson: externalLinks},
    });

    const {bgProcess} = await waitForJob({
      jobId,
      jobName,
    });

    if (validationErrors?.length) {
      bgProcess.warn(validationErrors);
    }
  } catch (e) {
    backgroundProcessService.startProcess(jobName).fail(e);
  }
}

function getAdTagsForCreativeTemplatePreview(
  templateId: number,
  previewParameters: AdTagPreviewParams = {},
  dynamicParameters?: DynamicParameter[],
  forcedDynamicMapping: Record<string, string> = {},
  forcedDPKey: string | null = null,
  forcedContentItemKeys: Record<string, string> = {},
) {
  const forcedDynamicParameters = dynamicParameters
    ? toRemoteDynamicParameters(dynamicParameters || [])
    : null;

  return apiV2Service
    .executePostRequest<CampaignAdTags>(
      `AdTags/${templateId}/GetAdTagsForTemplatePreview`,
      {},
      {
        previewParameters,
        forcedDynamicMapping: _mapKeys(forcedDynamicMapping, (_, format) => `_${format}`),
        ...(forcedDynamicParameters && {forcedDynamicParameters}),
        ...(forcedDPKey && {forcedDPKey}),
        ...(Object.keys(forcedContentItemKeys).length > 0 && {forcedContentItemKeys}),
      },
    )
    .then(({data}) => data);
}

async function uploadSubtitles({
  file,
  templateId,
  options = {},
}: {
  file: File;
  templateId: number;
  options?: ApiOptions;
}): Promise<string> {
  const {presignedUrl, finalS3Url} = await fileUploadApi.getPresignedUrlForUpload(CLMimeType.SRT);

  await uploadFileToS3({
    url: presignedUrl,
    file: file,
    contentType: CLMimeType.SRT,
    cancelToken: options?.cancelToken,
  });

  return addAssetFromUrl({
    templateId,
    assetType: TemplateAssetType.Subtitles,
    fileUrl: finalS3Url,
    fileName: file.name,
    options,
  });
}

async function uploadImage({
  templateId,
  file,
  options = {},
  imageUploadType = ImageUploadType.TemplateImage,
}: {
  templateId: number;
  file: File;
  options?: ApiOptions;
  imageUploadType?: ImageUploadType;
}): Promise<string> {
  const fileKey = await fileUploadApi.uploadImage({
    objectId: templateId,
    keyType: 'templateId',
    imageType: imageUploadType,
    file,
    options,
  });

  const {
    data: {url},
  } = await apiV2Service.executePostRequest<{url: string}>(
    `${templatesBasePath}/${templateId}/UploadPoster`,
    {},
    {fileKey},
    options,
  );

  return url;
}

async function uploadVideo({
  templateId,
  file,
  options,
  paramKey,
  width,
  height,
  extension = 'mp4',
  contentType = 'video/mp4',
}: {
  templateId: number;
  file: File;
  paramKey: string;
  width: number;
  height: number;
  options?: ApiOptions;
  extension?: string;
  contentType?: string;
}) {
  return fileUploadApi.uploadVideo({
    objectId: templateId,
    uploadType: VideoUploadType.TemplateVideo,
    file,
    options,
    paramKey,
    width,
    height,
    extension,
    contentType,
  });
}

function deleteDynamicParameter(paramKey: string, templateId: number) {
  return apiService.executeDeleteRequest<unknown>(
    `${templatesBasePath}/DeleteDynamicParameterFromTemplate`,
    {templateId, key: paramKey},
  );
}

function getUINameOptions(): Promise<DynamicParamNameOptions> {
  return apiService
    .executeGetRequest<[string]>(`${templatesBasePath}/GetStyleDynamicParametersUiNameOptions`)
    .then(({data}) => JSON.parse(data[0]) as RemoteDynamicParamNameOptions)
    .then(toClientDynamicParamsNameOptions);
}

function updateAllDynamicParameters(
  deletedDynamicParams: string[],
  newDynamicParameters: DynamicParameter[],
  updatedDynamicParameters: DynamicParameter[],
  dynamicParametersToRename: {paramKey: string; newName: string}[],
  templateId: number,
) {
  const url = createPathParamsUrl('Templates/:templateId/DynamicParameters/UpdateAll', {
    templateId,
  });

  const formObj = {
    keysOfDyanmicParametersToDelete: deletedDynamicParams,
    newDynamicParameters: newDynamicParameters.map(toRemoteDynamicParameter),
    updatedDynamicParameters: updatedDynamicParameters.map(toRemoteDynamicParameter),
    renameParameters: dynamicParametersToRename,
  };

  return apiV2Service.executePostRequest<unknown>(url, {}, formObj);
}

function isTemplateAllowedForUpdating(templateId: number): Promise<boolean> {
  return apiService
    .executeGetRequest<[boolean]>(`${templatesBasePath}/IsTemplateAllowedForUpdating`, {templateId})
    .then(({data: [isAllowedToUpdate]}) => isAllowedToUpdate);
}

function getFormatMapping(templateId: number, format: string, isJetMapping?: boolean) {
  const mappingPromise = apiV2Service
    .executeGetRequest<RemoteDPMapping>(
      `${templatesBasePath}/${templateId}/${format}/${isJetMapping ? 'Jet' : ''}Mapping`,
    )
    .then(({data}) => data);

  const metaPromise = apiV2Service
    .executeGetRequest<RemoteDPMeta>(`${templatesBasePath}/${templateId}/${format}/Metadata`)
    .then(({data}) => data);

  return Promise.all([mappingPromise, metaPromise]).then(([mapping, meta]) => ({
    meta: toClientMeta(meta),
    mapping: toClientMapping(mapping, meta),
  }));
}

function updateMapping(
  templateId: number,
  format: string,
  mapping: DPLayerMapping[],
  itemCount?: number,
) {
  return apiV2Service.executePutRequest(
    `${templatesBasePath}/${templateId}/_${format}/Mapping`,
    {},
    {...toRemoteMapping(mapping), ...(itemCount && {itemCount})},
  );
}

function updateTemporaryMapping(
  templateId: number,
  data: {format: string; mapping: DPLayerMapping[]; mappingKey: string}[],
) {
  return apiV2Service.executePostRequest(
    `AdTags/${templateId}/TempMapping`,
    {},
    {
      temporaryMappings: Object.fromEntries(
        data.map(({format, mappingKey, mapping}) => [
          `_${format}`,
          {
            key: mappingKey,
            tempMapping: toRemoteMapping(mapping),
          },
        ]),
      ),
    },
  );
}

function updateTemporaryDP(dynamicParameters: DynamicParameter[], key: string) {
  const tempDP = toRemoteDynamicParameters(dynamicParameters);

  return apiV2Service.executePostRequest(`AdTags/TempDP`, {}, {key, tempDP});
}

interface StoreTempContentItemParams {
  itemId: number;
  keysByAdSize: Record<string, string>;
}

async function storeTempContentItem({
  itemId,
  keysByAdSize,
}: StoreTempContentItemParams): Promise<void> {
  await apiV2Service.executePostRequest(`AdTags/TempContentItem`, {}, {itemId, keysByAdSize});
}

function updateFormatProductCount(templateId: number, format: string, count: number) {
  return apiV2Service.executePutRequest(
    `${templatesBasePath}/${templateId}/_${format}/itemCount/${count}`,
  );
}

function updateAllFormatsProductCount({
  templateId,
  minProductCount,
  maxProductCount,
}: {
  templateId: number;
  minProductCount: number;
  maxProductCount: number;
}) {
  return apiV2Service.executePutRequest(
    `${templatesBasePath}/${templateId}/allformats/itemCount`,
    {minItemCount: minProductCount, itemCount: maxProductCount},
    {},
  );
}

function duplicateTemplateToAgencyWithFormats({templateId}: {templateId: number}): Promise<number> {
  return apiV2Service
    .executePostRequest<JobExecutionParams>(
      `${templatesBasePath}/duplicate/${templateId}`,
      {},
      {
        templateId,
        includeFormats: true,
      },
    )
    .then(({data: {executionId: jobId}}) => {
      return waitForJob({jobId, jobName: `Duplicate Template ${templateId}`});
    })
    .then(({result}) => (result as JobTemplateResult).id);
}

function duplicateTemplateToAgencyWithoutFormats({templateId}: {templateId: number}) {
  return apiV2Service
    .executePostRequest<number>(
      `${templatesBasePath}/duplicateWithoutFormats/${templateId}`,
      {},
      {
        templateId,
        includeFormats: false,
      },
    )
    .then(({data}) => data);
}

function duplicateTemplateToAgency({
  includeFormats,
  ...params
}: {
  templateId: number;
  includeFormats: boolean;
}) {
  if (includeFormats) return duplicateTemplateToAgencyWithFormats(params);

  return duplicateTemplateToAgencyWithoutFormats(params);
}

function reGeneratePoster(templateId: number) {
  return apiV2Service
    .executePostRequest<JobExecutionParams>(`${templatesBasePath}/${templateId}/RefreshPoster`)
    .then(({data}) => data)
    .then(({executionId: jobId}) => {
      return waitForJob({
        jobId,
        jobName: `Refresh Poster for Template ${templateId}`,
      }).catch(() => {});
    })
    .then(() => get({templateId}))
    .then(({data}) => data.poster);
}

function reGeneratePosters(
  templateIds: number[],
  onPosterUpdated: (data: {templateId: number; poster: string}) => void,
) {
  return Promise.all(
    templateIds.map((templateId) =>
      apiV2Service
        .executePostRequest<JobExecutionParams>(`${templatesBasePath}/${templateId}/RefreshPoster`)
        .then(({data: {executionId: jobId}}) => ({jobId, templateId}))
        .catch((e: ApiError): null => {
          handleApiError(e);

          return null;
        }),
    ),
  )
    .then((jobs) => jobs.filter((data) => !!data?.jobId))
    .then((jobs) => {
      return jobs.map(
        (job) =>
          job &&
          waitForJob({
            jobId: job.jobId,
            jobName: `Refresh Poster for Template ${job.templateId}`,
          })
            .then(() => get({templateId: job.templateId}))
            .then(({data}) => onPosterUpdated({templateId: job.templateId, poster: data.poster})),
      );
    });
}

function triggerVerificationJob(templateId: number) {
  apiV2Service.executePostRequest<string>(`${templatesBasePath}/${templateId}/RefreshPoster`);
}

async function importCreativeTemplate(
  templateZip: PreviewFile,
  externalLinks: ExternalLink[] = [],
) {
  if (!templateZip.file) {
    throw new Error('Template file is required for import');
  }

  return apiV2Service
    .uploadFile<JobExecutionParams>({
      endpoint: `${templatesBasePath}/Import`,
      params: {},
      file: templateZip.file,
      formDataKey: 'zipFile',
      postData: {externalLinksJson: externalLinks},
    })
    .then(({data}) => data);
}

function exportCreativeTemplate(templateId: number) {
  return apiV2Service
    .executeGetRequest<JobExecutionParams>(`${templatesBasePath}/${templateId}/Export`)
    .then(({data}) => data);
}

function publishFigma(templateId: number, file: File) {
  const jobName = `Publish Formats to Template ${templateId}`;

  return apiV2Service
    .uploadFile<JobExecutionParams>({
      endpoint: `Publishing/Figma/${templateId}`,
      file,
      formDataKey: 'figmaZip',
      params: {},
    })
    .then(({data: {executionId: jobId}}) => {
      return waitForJob({
        jobId,
        jobName,
      }).catch(() => {});
    })
    .catch((e) => {
      backgroundProcessService.startProcess(jobName).fail(e);
    });
}

function getTemplateMovies(templateId: number) {
  return apiV2Service
    .executeGetRequest<Record<string, Movie>>(`${templatesBasePath}/${templateId}/MoviesByFormat`)
    .then(({data}) => data);
}

async function addAssetFromUrl({
  templateId,
  assetType,
  fileUrl,
  fileName,
  options = {},
}: {
  templateId: number;
  assetType: TemplateAssetType;
  fileUrl: string;
  fileName: string;
  options?: ApiOptions;
}): Promise<string> {
  const {data} = await apiV2Service.executePutRequest<string>(
    `AdBuilder/${templatesBasePath}/${templateId}/AssetFromUrl`,
    {templateId: templateId, fileUrl, type: assetType, fileName},
    {},
    options,
  );

  return data;
}

function addAsset(
  templateId: number,
  file: File,
  assetType: TemplateAssetType,
  font?: ComplexTextFont,
  customMetadata?: AssetMetadata,
): Promise<TemplateAsset> {
  if (assetType === TemplateAssetType.Video) {
    return addVideoAssetWithPresignedUrl(templateId, file, customMetadata);
  }

  const payload: ParamsObj = {'metadata.name': file.name, 'metadata.type': assetType};

  if (font) {
    for (const [key, value] of Object.entries(font)) {
      payload[`metadata.${key}`] = value;
    }
  }

  if (customMetadata) {
    payload['metadata.customMetadata'] = customMetadata;
  }

  return apiV2Service
    .uploadFile<RemoteTemplateAsset>({
      endpoint: `AdBuilder/${templatesBasePath}/${templateId}/Assets`,
      params: {},
      postData: payload,
      formDataKey: 'file',
      options: {},
      file,
      method: 'put',
    })
    .then(({data}) => toClientTemplateAsset(data));
}

async function addVideoAssetWithPresignedUrl(
  templateId: number,
  file: File,
  customMetadata?: AssetMetadata,
) {
  const {presignedUrl, finalS3Url} = await fileUploadApi.getPresignedUrlForUpload(file.type);

  await uploadFileToS3({
    url: presignedUrl,
    file: file,
    contentType: file.type,
  });

  const {
    data: {executionId},
  } = await apiV2Service.executePutRequest<JobExecutionParams>(
    `AdBuilder/${templatesBasePath}/${templateId}/AssetsJob`,
    {
      type: TemplateAssetType.Video,
      name: file.name,
      url: finalS3Url,
      customMetadata,
    },
    {},
  );

  const {result} = await waitForJobWithoutBGProcess<RemoteTemplateAsset & JobResult>({
    jobExecutionId: executionId,
  });

  return toClientTemplateAsset(result);
}

function deleteAsset(templateId: number, assetId: string) {
  return apiV2Service.executeDeleteRequest(
    `AdBuilder/${templatesBasePath}/${templateId}/Assets/${assetId}`,
  );
}

export type AssetMetadata = {
  ContentDescription: string;
  Source: string;
};

async function updateAssetMetadata(
  clientId: string,
  templateId: number,
  assetId: string,
  assetType: string,
  metadata: AssetMetadata,
): Promise<void> {
  await apiV2Service.executePutRequest<void>(
    `AdBuilder/${templatesBasePath}/${templateId}/Assets/${assetId}/CustomMetadata`,
    {assetType},
    metadata,
  );
}

function saveMovie(templateId: number, format: string, movie: Movie) {
  return apiV2Service.executePutRequest(
    `AdBuilder/${templatesBasePath}/${templateId}/formats/${format}`,
    {},
    {...movie},
  );
}

function saveMovieWithPosition(
  templateId: number,
  format: string,
  movie: Movie,
  previewPosition?: {x: number; y: number},
) {
  return apiV2Service.executePutRequest(
    `AdBuilder/${templatesBasePath}/${templateId}/formatsV2/${format}`,
    {},
    {movie, previewPosition},
  );
}

function getDPOptionsEmbeddings() {
  return aiApi.getEmbedding(EmbeddingSourceType.DynamicParamOption).then((res) =>
    res.map(({data, jsonMetadata}) => {
      const {name, category, type}: {name: string; category: string; type: DynamicParametersType} =
        JSON.parse(jsonMetadata);

      return {
        name,
        category,
        type: +type,
        embedding: data,
      };
    }),
  );
}

function renameDynamicParameter(templateId: number, paramKey: string, newName: string) {
  return apiV2Service.executePatchRequest(
    `${templatesBasePath}/${templateId}/DynamicParameters/Rename`,
    {},
    {paramKey, newName},
  );
}

function updateLayerMetadata(
  templateId: number,
  format: string,
  data: {
    compId: number;
    layerId: number;
    isLocked: boolean;
  }[],
) {
  return apiV2Service.executePostRequest(
    `AdBuilder/${templatesBasePath}/${templateId}/Formats/_${format}/UpdateLayerMetadata`,
    {},
    {data},
  );
}

async function getFromLibrary(): Promise<CreativeTemplate[]> {
  const [templateEnums, {data}] = await Promise.all([
    enumsService.loadEnums(CLEnumType.Templates),
    apiV2Service.executeGetRequest<RemoteCreativeTemplate[]>(`${templatesBasePath}/GetFromLibrary`),
  ]);

  return _map(data, (creativeTemplate) =>
    toClientCreativeTemplate(creativeTemplate, templateEnums),
  );
}

function updateShareStatus(
  templateId: number,
  targetClientId: number,
  sharePermission: SharingType,
  isOn: boolean,
) {
  const method = isOn ? apiV2Service.executePostRequest : apiV2Service.executeDeleteRequest;

  return method(`${templatesBasePath}/permission`, {templateId, targetClientId, sharePermission});
}

function getIntersectedClients(templateIds: number[]) {
  return apiV2Service
    .executeGetRequest<SharedClient[]>(`${templatesBasePath}/GetIntersectedClients`, {templateIds})
    .then((res) => res.data);
}

function getRealtimePreviewFromMovie(movie: Movie): Promise<string> {
  const mainComp = movie.comps[0];
  const adFormat = `_${mainComp.w}x${mainComp.h}`;

  return apiV2Service
    .executePostRequest<string>(`AdBuilder/GetTempRealtimeVideoUrlByMovie`, {adFormat}, {
      ...movie,
    } as PostDataObj)
    .then((res) => res.data);
}

function getPublishedAEMovie(
  publishedXmlFile: File,
  aepxFile: File,
  assetMappings: AELayerAssetsMapping[],
) {
  return apiV2Service
    .executePostRequest<Movie>(
      `AdBuilder/GetPublishedMovie`,
      {},
      {publishedXmlFile, aepxFile, assetMappings: JSON.stringify(assetMappings)},
      {sendAsFormData: true},
    )
    .then((res) => res.data);
}

async function getSupportedAdSizes() {
  const {
    data: [adSizes],
  } = await apiService.executeGetRequest<AdSize[][]>(`${aeExtensionBasePath}/GetSupportedFormats`);

  return adSizes;
}

async function getActivityLog(templateId: number) {
  const {
    data: {items},
  } = await apiV2Service.executeGetRequest<{items: RemoteTemplateLogItem[]}>(
    `${templatesBasePath}/${templateId}/activityLog`,
  );

  return _map(items, toClientActivityLog);
}

async function addActivityLog({
  templateId,
  file,
  affectedAdSizes,
  publishType,
}: {
  templateId: number;
} & ActivityLogData) {
  const {data} = await apiV2Service.executePutRequest<void>(
    `${templatesBasePath}/${templateId}/activityLog`,
    {},
    {file, request: {affectedAdSizes, publishType, actionType: TemplateActivityLogType.Publish}},
    {sendAsFormData: true},
  );

  return data;
}

async function renameCreativeTemplates(creativeTemplates: EntityToRename[]) {
  const templatesToRename = creativeTemplates.map((template) => ({
    id: template.id,
    newName: template.newName,
  }));

  return apiV2Service.executePatchRequest<void>(
    `${templatesBasePath}/batchRename`,
    {},
    {data: templatesToRename},
  );
}

async function generateDpAndMapToLayers({
  templateId,
}: {
  templateId: number;
}): Promise<Record<string, GeneratedDynamicParams[]>> {
  const {
    data: {data: dynamicParametersPerFormat},
  } = await apiV2Service.executeGetRequest<{data: Record<string, RemoteGeneratedDynamicParams>}>(
    `${templatesBasePath}/${templateId}/GenerateDpAndMapToLayersForFormats`,
  );

  return toClientGeneratedDynamicParamsByFormat(dynamicParametersPerFormat);
}

export const creativeTemplatesApi = {
  getAll,
  get,
  getTemplateDropDownOptions,
  validatePublicationAllowed,
  deleteTemplateClient,
  create,
  update,
  deleteTemplates,
  duplicateTemplates,
  addToClient,
  deleteFormat,
  deleteFormats,
  uploadExternalAd,
  getAdTagsForCreativeTemplatePreview,
  uploadImage,
  uploadVideo,
  uploadSubtitles,
  getUINameOptions,
  deleteDynamicParameter,
  isTemplateAllowedForUpdating,
  getFormatMapping,
  getDynamicSourceOptions,
  updateMapping,
  updateTemporaryMapping,
  updateTemporaryDP,
  storeTempContentItem,
  updateFormatProductCount,
  duplicateTemplateToAgency,
  updateAllDynamicParameters,
  reGeneratePoster,
  reGeneratePosters,
  importCreativeTemplate,
  exportCreativeTemplate,
  publishFigma,
  updateAllFormatsProductCount,
  getTemplateMovies,
  addAsset,
  deleteAsset,
  updateAssetMetadata,
  saveMovie,
  getDPOptionsEmbeddings,
  renameDynamicParameter,
  updateLayerMetadata,
  getFromLibrary,
  getAllWithPermissions,
  updateShareStatus,
  triggerVerificationJob,
  getIntersectedClients,
  getRealtimePreviewFromMovie,
  getPublishedAEMovie,
  getSupportedAdSizes,
  getActivityLog,
  addActivityLog,
  renameCreativeTemplates,
  generateDpAndMapToLayers,
  saveMovieWithPosition,
};
